import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Victory Video Composable
 *
 * Manages AI-generated celebration videos for the game-won screen.
 * Supports multiple video styles (heist, gallery, royalty) with
 * per-video text positioning for dynamic overlays.
 *
 * ★ Insight ─────────────────────────────────────
 * Video preloading is critical for smooth celebrations:
 * - Uses <link rel="preload"> for browser-level caching
 * - Falls back gracefully to Three.js trophy if videos fail
 * - Tracks load state per video for intelligent selection
 * ─────────────────────────────────────────────────
 */

export type CelebrationStyle = 'heist' | 'gallery' | 'royalty'

export interface TextPosition {
  x: string
  y: string
  align?: 'left' | 'center' | 'right'
}

export interface CelebrationVideo {
  id: string
  style: CelebrationStyle
  src: string
  srcFallback?: string // MP4 fallback for Safari
  textPosition: TextPosition
  scorePosition: TextPosition
  duration: number // Expected duration in seconds
  loaded: boolean
}

// Video registry - add entries here as videos are generated
// Currently available: royalty-1.mp4 (1.2MB), gallery-1.mp4 (3.3MB)
const VIDEO_REGISTRY: Omit<CelebrationVideo, 'loaded'>[] = [
  // Card Royalty Court - Playing card royal figurines
  // Source: Playing_Card_Royal_Figurines_Generated.mp4 (compressed)
  {
    id: 'royalty-1',
    style: 'royalty',
    src: '/videos/celebrations/royalty-1.mp4',
    textPosition: { x: '50%', y: '12%', align: 'center' },
    scorePosition: { x: '50%', y: '22%', align: 'center' },
    duration: 8,
  },

  // Championship Gallery - Figurine celebration
  // Source: Video_Generation_With_Specific_Ethnicity.mp4 (compressed)
  {
    id: 'gallery-1',
    style: 'gallery',
    src: '/videos/celebrations/gallery-1.mp4',
    textPosition: { x: '50%', y: '78%', align: 'center' },
    scorePosition: { x: '50%', y: '88%', align: 'center' },
    duration: 8,
  },

  // TODO: Add more videos as they are generated
  // The Heist Complete - Ocean's Eleven cool (future)
  // {
  //   id: 'heist-1',
  //   style: 'heist',
  //   src: '/videos/celebrations/heist-1.mp4',
  //   textPosition: { x: '50%', y: '15%', align: 'center' },
  //   scorePosition: { x: '50%', y: '25%', align: 'center' },
  //   duration: 7,
  // },
]

export function useVictoryVideo() {
  const videos = ref<CelebrationVideo[]>(
    VIDEO_REGISTRY.map(v => ({ ...v, loaded: false }))
  )
  const currentVideo = ref<CelebrationVideo | null>(null)
  const isLoading = ref(false)
  const hasVideosAvailable = ref(false)
  const preloadLinks: HTMLLinkElement[] = []

  /**
   * Check if a video file exists by attempting to fetch headers
   */
  async function checkVideoExists(src: string): Promise<boolean> {
    try {
      const response = await fetch(src, { method: 'HEAD' })
      return response.ok
    } catch {
      return false
    }
  }

  /**
   * Preload all registered videos and track which ones are available
   */
  async function preloadVideos(): Promise<void> {
    isLoading.value = true

    const checkPromises = videos.value.map(async (video) => {
      const exists = await checkVideoExists(video.src)
      video.loaded = exists

      if (exists) {
        // Add preload link for browsers to cache
        const link = document.createElement('link')
        link.rel = 'preload'
        link.href = video.src
        link.as = 'video'
        link.type = 'video/webm'
        document.head.appendChild(link)
        preloadLinks.push(link)
      }

      return exists
    })

    const results = await Promise.all(checkPromises)
    hasVideosAvailable.value = results.some(r => r)
    isLoading.value = false
  }

  /**
   * Get all available (loaded) videos
   */
  function getAvailableVideos(): CelebrationVideo[] {
    return videos.value.filter(v => v.loaded)
  }

  /**
   * Get available videos filtered by style
   */
  function getVideosByStyle(style: CelebrationStyle): CelebrationVideo[] {
    return videos.value.filter(v => v.loaded && v.style === style)
  }

  /**
   * Select a random video from available options
   * Optionally filter by style
   */
  function selectRandom(style?: CelebrationStyle): CelebrationVideo | null {
    const available = style
      ? getVideosByStyle(style)
      : getAvailableVideos()

    if (available.length === 0) {
      return null
    }

    const selected = available[Math.floor(Math.random() * available.length)]
    currentVideo.value = selected ?? null
    return currentVideo.value
  }

  /**
   * Select a specific video by ID
   */
  function selectById(id: string): CelebrationVideo | null {
    const video = videos.value.find(v => v.id === id && v.loaded)
    currentVideo.value = video ?? null
    return currentVideo.value
  }

  /**
   * Get the appropriate video source based on browser support
   * Safari prefers MP4, others prefer WebM
   */
  function getVideoSource(video: CelebrationVideo): string {
    // Check for Safari (doesn't support WebM well)
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

    if (isSafari && video.srcFallback) {
      return video.srcFallback
    }

    return video.src
  }

  /**
   * Clean up preload links when done
   */
  function cleanup(): void {
    preloadLinks.forEach(link => {
      link.remove()
    })
    preloadLinks.length = 0
  }

  // Auto-preload on mount
  onMounted(() => {
    preloadVideos()
  })

  // Cleanup on unmount
  onUnmounted(() => {
    cleanup()
  })

  return {
    videos,
    currentVideo,
    isLoading,
    hasVideosAvailable,
    preloadVideos,
    getAvailableVideos,
    getVideosByStyle,
    selectRandom,
    selectById,
    getVideoSource,
    cleanup,
  }
}
